import { Injectable } from "@angular/core";

import { TranslateService } from "@ngx-translate/core";

import * as CubiqTS from "shared/cubiq-model-multi-asset";

import {
  TimezonePromiseLookupService,
  MarketTimePromiseLookupService,
  CcyPairPromiseLookupService,
  TraderGroupPrimaryLookupService,
  MarketTimeLookupService,
  ActiveUserProfilePromiseService,
  ActiveUser,
  SystemConfigPromiseService,
  SysFeatures,
} from "shared/cubiq-services";

/**
 * IMPORTANT: This service should NOT contain reference to any Order entry services except Utility.
 */

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { ICGoodFromUntilModel } from "../../interface/controls/models/bfx-good-from-until.model";
import { BenchmarkFixingTime } from "shared/cubiq-services/lookup/model/impl/BenchmarkFixingPrimaryLookupService";
import { ICTimeControlModel } from "../../interface/controls/models/bfx-interface-controls.models";

@Injectable()
export class OETimeMachineService {
  private _marketTimeConstLookupService: MarketTimeLookupService;

  /**********************************************************************/
  /* DATA */
  private config: any = {
    debug: true,
    dateTimeFormats: [
      "DD MMM YYYY HH:mm",
      "DD MMM YYYY HH:mm:ss",
      "DD-MMM-YYYY HH:mm",
      "DD-MMM-YYYY HH:mm:ss",
      "DD/MMM/YYYY HH:mm",
      "DD/MMM/YYYY HH:mm:ss",
      "DD MMM YY HH:mm",
      "DD MMM YY HH:mm:ss",
      "DD-MMM-YY HH:mm",
      "DD-MMM-YY HH:mm:ss",
      "DD/MMM/YY HH:mm",
      "DD/MMM/YY HH:mm:ss",
      "DD MMM YYYY HHmm",
      "DD MMM YYYY HHmmss",
      "DD-MMM-YYYY HHmm",
      "DD-MMM-YYYY HHmmss",
      "DD/MMM/YYYY HHmm",
      "DD/MMM/YYYY HHmmss",
      "DD MMM YY HHmm",
      "DD MMM YY HHmmss",
      "DD-MMM-YY HHmm",
      "DD-MMM-YY HHmmss",
      "DD/MMM/YY HHmm",
      "DD/MMM/YY HHmmss",
      "YYYYMMDD HHmm",
    ],
  };

  private dataset: {
    timeZoneNames: Array<string>;
    timeZoneImpls: Array<CubiqTS.TimeZoneImpl>;
  } = {
    timeZoneNames: null, // Array<string>
    timeZoneImpls: null,
  };
  activeUser: ActiveUser;

  /**********************************************************************/
  /* CONSTRUCTOR  */
  constructor(
    private _timezoneLookupService: TimezonePromiseLookupService,
    private _marketTimeConstPromiseLookupService: MarketTimePromiseLookupService,
    protected _activeUserService: ActiveUserProfilePromiseService,
    protected _ccyPairLookupService: CcyPairPromiseLookupService,
    protected _traderGroupLookupService: TraderGroupPrimaryLookupService,
    private _sysConf: SystemConfigPromiseService,
    protected translationService: TranslateService,
  ) {
    this.initialize();
  }

  /**********************************************************************/
  /* INITIALIZATION */
  private initialize() {
    this._marketTimeConstLookupService = this._marketTimeConstPromiseLookupService.getBase();
    this.loadTimeZones().then(() => {});

    this._activeUserService.getActiveUserData().then((activeUser) => {
      this.activeUser = activeUser;
    });
  }

  private loadTimeZones(): Promise<boolean> {
    return new Promise<boolean>((res) => {
      this._timezoneLookupService.isPrimed().then(() => {
        this._timezoneLookupService.getAll().then((_timeZones: Array<CubiqTS.TimeZoneImpl>) => {
          if (_timeZones != null && Array.isArray(_timeZones) && _timeZones.length > 0) {
            this.dataset.timeZoneImpls = _timeZones;
            this.dataset.timeZoneNames = _timeZones.map((item) => item.id);
          }
          res(true);
        });
      });
    });
  }

  public loadMarketTimeConstants(
    calendar: CubiqTS.CurrencyPairCalendarImpl = null,
    DAYbase: string = null,
    DAYnonBase: string = null,
    includeGTC: boolean = false,
    dayInUserTZ: boolean = true,
  ): {
    names: Array<string>;
    constants: Array<CubiqTS.MarketTimeConstantImpl>;
  } {
    let marketTimeConstantNames: Array<string> = new Array<string>();
    let marketTimeConstantImpls: Array<CubiqTS.MarketTimeConstantImpl> =
      new Array<CubiqTS.MarketTimeConstantImpl>();

    // ---------------
    // Push in the DAY entry
    // if one specified in params
    if (null != calendar && null != DAYbase && null != DAYnonBase) {
      // load the Trade Date Roll for the pair,

      let DAYDate: CubiqTS.StandardDate = calendar.tradeDateRollDate;
      let DAYTime: CubiqTS.StandardTime = calendar.tradeDateRollTime;

      let hol: Date = new Date(DAYDate.year, DAYDate.month - 1, DAYDate.day);
      let dateAsMoment = CubiqTS.moment(hol, "DD-MM-YYYY");
      let timeAsLong: CubiqTS.Long = new CubiqTS.Long(DAYTime.hours * 100 + DAYTime.minutes);
      let timeZone = DAYTime.timeZone;

      if (null != DAYDate && null != DAYTime) {
        // make a marketTimeConstant and push it in

        try {
          let zone = this.getTimeZoneImpl(DAYTime.timeZone);
          if (dayInUserTZ === true && zone != null) {
            let dayDateMoment = CubiqTS.moment.tz(CubiqTS.moment.now(), zone.javaTimeZoneCd);
            dayDateMoment.set({
              year: DAYDate.year,
              month: DAYDate.month - 1,
              date: DAYDate.day,
              hour: DAYTime.hours,
              minute: DAYTime.minutes,
              second: 0,
              millisecond: 0,
            });

            let sysTdrZone: string = this._sysConf.hardGetSystemConfig(SysFeatures.systemTDRZone);
            let tdrZone: string = sysTdrZone ?? "EST";
            timeZone = this.activeUser?.timeZoneCd ?? tdrZone;
            let userTz = this.getTimeZoneImpl(timeZone);
            let dayMoment = dayDateMoment.tz(userTz.javaTimeZoneCd);

            let now = CubiqTS.moment();
            while (dayMoment.isSameOrBefore(now)) {
              dayMoment = dayMoment.add(1, "d");
            }

            dateAsMoment = dayMoment;
            timeAsLong = new CubiqTS.Long(dateAsMoment.hour() * 100 + dateAsMoment.minutes());
          }
        } catch (e) {
          logger.out.error(
            `${this.constructor.name}: Error converting day to user defined ` + `time zone ${e}`,
            e,
          );
        }

        let DAY = new CubiqTS.MarketTimeConstantImpl();

        DAY.constantDate = this.convertDateToUTCDate(dateAsMoment); // moment
        DAY.constantTime = timeAsLong; // long
        DAY.timeZoneCd = timeZone; // string
        DAY.constantName = "DAY"; // string

        // this.dataset.marketTimeConstantNames.push("DAY");
        marketTimeConstantImpls.push(DAY);
      }
    } else {
      let DAY = new CubiqTS.MarketTimeConstantImpl();
      DAY.constantName = "DAY"; // string
      marketTimeConstantImpls.push(DAY);
    }

    if (includeGTC) {
      let GTC = new CubiqTS.MarketTimeConstantImpl();
      GTC.constantDate = null; // moment
      GTC.constantTime = null; // long
      GTC.timeZoneCd = null; // string
      GTC.constantName = "GTC"; // string
      marketTimeConstantImpls.push(GTC);
    }

    if (!includeGTC) {
      let NOW = new CubiqTS.MarketTimeConstantImpl();
      NOW.constantDate = null; // moment
      NOW.constantTime = null; // long
      NOW.timeZoneCd = null; // string
      NOW.constantName = "NOW"; // string
      marketTimeConstantImpls.push(NOW);
    }
    // ---------------
    // And any market constants

    let _marketTimeConstants: Array<CubiqTS.MarketTimeConstantImpl> =
      this._marketTimeConstLookupService.getAll();

    if (
      _marketTimeConstants != null &&
      Array.isArray(_marketTimeConstants) &&
      _marketTimeConstants.length > 0
    ) {
      _marketTimeConstants.forEach((item: CubiqTS.MarketTimeConstantImpl) => {
        if (this.validateMarketTimeConstantImpl(item)) {
          marketTimeConstantImpls.push(item);
          marketTimeConstantNames.push(item.constantName);
        }
      });
    }

    return {
      names: marketTimeConstantNames,
      constants: marketTimeConstantImpls,
    };
  }

  /**
   * Create a UTC Moment with same date as the date defined in local date - for MarketTimeConsts,
   * all dates are assumed to be in UTC format
   * @param localDate
   * @returns
   */
  private convertDateToUTCDate(localDate: CubiqTS.Moment): CubiqTS.Moment {
    const utcDate = CubiqTS.moment.utc();
    // Copy in date part
    utcDate.year(localDate.year());
    utcDate.month(localDate.month());
    utcDate.date(localDate.date());

    // Clear any time part
    utcDate.startOf("date");

    return utcDate;
  }

  /**********************************************************************/
  /**
   * Only for OE, not settings
   * @param marketTimeConstantImpl
   */
  public validateMarketTimeConstantImpl(
    marketTimeConstantImpl: CubiqTS.MarketTimeConstantImpl,
  ): boolean {
    try {
      let nowMoment = CubiqTS.moment();
      let marketMoment = this.createMomentForMarketTimeConstantImpl(marketTimeConstantImpl);

      if (
        nowMoment != null &&
        nowMoment instanceof CubiqTS.moment &&
        nowMoment.isValid() &&
        marketMoment != null &&
        marketMoment instanceof CubiqTS.moment &&
        marketMoment.isValid()
      ) {
        // REMOVED AS PART OF OMS-29010; RESTORED AS A PART OF G-F-U UNTIL
        return nowMoment.isBefore(marketMoment);
      }
      return false;
    } catch (ex) {
      logger.out.error(`ERROR: _oeUtility.marketTimeConstantImpl(): Exception: ${ex}`);
      return false;
    }
  }

  public validateICGoodFromUntilModel(model: ICGoodFromUntilModel): boolean {
    try {
      let nowMoment = CubiqTS.moment();
      let marketMoment = this.createMomentICGoodFromUntilModel(model);

      if (
        nowMoment != null &&
        nowMoment instanceof CubiqTS.moment &&
        nowMoment.isValid() &&
        marketMoment != null &&
        marketMoment instanceof CubiqTS.moment &&
        marketMoment.isValid()
      ) {
        return nowMoment.isBefore(marketMoment);
      }
      return false;
    } catch (ex) {
      logger.out.error(`ERROR: _oeUtility.marketTimeConstantImpl(): Exception: ${ex}`);
      return false;
    }
  }

  createMomentICGoodFromUntilModel(model: ICGoodFromUntilModel) {
    const timeValue =
      model?.timeValue != null &&
      model?.timeValue instanceof ICTimeControlModel &&
      typeof model.timeValue.timeValue === "string"
        ? model.timeValue.timeValue
        : typeof model?.timeValue === "string"
          ? model.timeValue
          : null;

    if (
      typeof model?.dateValue !== "string" ||
      timeValue == null ||
      typeof model?.timezoneValue !== "string"
    )
      return null;

    return this.createMoment4StringArray(model.dateValue, timeValue, model?.timezoneValue);
  }

  /**********************************************************************/
  /* ACTIVATION/EXPIRY Methods */

  /* TimeZones */
  public getTimeZoneNames(): Array<string> {
    return this.dataset.timeZoneNames;
  }

  public getTimeZoneImpls(): Array<CubiqTS.TimeZoneImpl> {
    return this.dataset.timeZoneImpls;
  }

  public isTimeZoneNameValid(tz: string | { value: string }): boolean {
    let dataset = this.dataset.timeZoneNames;
    const predicate = typeof tz === "string" ? (item) => item === tz : (item) => item === tz?.value;

    return dataset != null ? dataset.find(predicate) != null : false;
  }
  public getTimeZoneImpl(tz: string): CubiqTS.TimeZoneImpl {
    if (this.isTimeZoneNameValid(tz)) {
      let dataset = this.dataset.timeZoneImpls.slice();
      let result =
        dataset != null && dataset.length > 0 ? dataset.find((item) => item.id === tz) : null;
      return result != null ? result : null;
    } else {
      return null;
    }
  }

  /* Benchmark Fixing Date and Time Validations */

  /**
   * @description Returns true if the Fixing Date Time value is in the past.
   * *NOTE: not used, leaving for possible future use.
   */
  public isFixingPast(fixingDate: Partial<{ value: Date }>, fixing: any): boolean {
    let isFixingDateInvalid: boolean;
    let isFixingTimeInvalid: boolean;
    if (fixingDate && fixingDate.value) {
      isFixingDateInvalid = this.isFixingDatePast(fixingDate.value);

      if (fixing && fixing.value) {
        isFixingTimeInvalid = this.isFixingTimePast(fixingDate, fixing.fixing);
      }
    }
    return isFixingDateInvalid && isFixingTimeInvalid;
  }

  /**
   * @description Returns true if the date is in the past, relative to now.
   * @param fixingDate the fixing Date being validated from the OE.
   */
  public isFixingDatePast(fixingDate: Date): boolean {
    let now: Date = new Date();
    // see if the date is before today
    let today: Date = new Date(now.valueOf());
    today.setHours(0, 0, 0, 0);
    // if temp is older date than today
    return fixingDate && fixingDate.valueOf() < today.valueOf();
  }

  /**
   * @description Returns true the fixing time that is being validated is in the past, relative to now.
   * @param fixingDate is the defaultFixingDate property from OEPanelBmark model needed for full date comparison
   * @param fixingTime the fixing time that is being validated.
   */
  public isFixingTimePast(
    fixingDate: Partial<{ value: Date }>,
    fixingTime: BenchmarkFixingTime | string,
  ): boolean {
    let ret: boolean = false;
    let now: Date = new Date();

    try {
      if (fixingTime != null) {
        let timezoneCd: string =
          fixingTime instanceof BenchmarkFixingTime
            ? fixingTime.timezoneCd
            : fixingTime.slice(fixingTime.length - 3);
        let fixingTimeNumber: number =
          fixingTime instanceof BenchmarkFixingTime
            ? fixingTime.localTime.toNumber()
            : +fixingTime.substring(0, 4).replace(/:/g, "");
        let timeZone: CubiqTS.TimeZoneImpl = this.dataset.timeZoneImpls.find(
          (timezone) => timezone.id == timezoneCd,
        );

        if (timeZone != null && fixingTimeNumber != null) {
          let fixingDateValue: Date = fixingDate && fixingDate.value ? fixingDate.value : now;
          let fixingDateStr: string =
            fixingDateValue.getFullYear() +
            "-" +
            ("0" + (fixingDateValue.getMonth() + 1)).slice(-2) +
            "-" +
            ("0" + fixingDateValue.getDate()).slice(-2);
          fixingDateStr +=
            "T" +
            ("0" + (fixingTimeNumber - (fixingTimeNumber % 100)) / 100).slice(-2) +
            ":" +
            ("0" + (fixingTimeNumber % 100)).slice(-2);
          let currentTimeUTC: CubiqTS.Moment = CubiqTS.moment().utc();
          let fixingTimeUTC: CubiqTS.Moment = CubiqTS.moment
            .tz(fixingDateStr, timeZone.javaTimeZoneCd)
            .utc();
          return fixingTimeUTC.isBefore(currentTimeUTC);
        }
      }
    } catch (ex) {
      logger.out.warn("Failed to check fixing time against local date ", ex);
    }

    // see if the date is before today
    let today: Date = new Date(now.valueOf());
    today.setHours(0, 0, 0, 0);
    let fixingTimeNumber: number =
      fixingTime instanceof BenchmarkFixingTime
        ? fixingTime.localTime.toNumber()
        : +fixingTime.substring(0, 4).replace(/:/g, "");

    // see if time is before now
    let nowTimeStr: string = "" + now.getHours() + now.getMinutes();
    ret = fixingTimeNumber < parseInt(nowTimeStr);
    return ret;
  }

  public isMarketTimeConstantNameValid(mtc: string, caseInsensitive: boolean = false): boolean {
    let dataset: string[] = this._marketTimeConstLookupService
      .getAll()
      .map((marketConst: CubiqTS.MarketTimeConstantImpl) => marketConst.constantName);
    dataset = dataset.concat(["DAY", "NOW", "GTC"]);
    return dataset != null && dataset.length > 0
      ? dataset.find(
          (item) => item === mtc || (caseInsensitive && item.toLowerCase() === mtc.toLowerCase()),
        ) != null
      : false;
  }

  public isMarketTimeConstantIdValid(mtcId: string): boolean {
    let dataset: Array<CubiqTS.MarketTimeConstantImpl> =
      this._marketTimeConstLookupService.getAll();
    return dataset != null && dataset.length > 0
      ? dataset.find((item) =>
          item.id instanceof CubiqTS.Long ? item.id.toString() === mtcId : item.id === mtcId,
        ) != null
      : false;
  }

  public getMarketTimeConstantImpl(mtc: string): CubiqTS.MarketTimeConstantImpl {
    let dataset = this._marketTimeConstLookupService.getAll();
    ["DAY", "NOW", "GTC"].forEach((name: string) => {
      let marketConstant: CubiqTS.MarketTimeConstantImpl = new CubiqTS.MarketTimeConstantImpl();
      marketConstant.constantName = name;
      if (name === "DAY") {
        marketConstant.constantType = CubiqTS.MarketTimeConstantType.TODAY;
      }
      dataset.push(marketConstant);
    });
    let result =
      dataset != null && dataset.length > 0
        ? dataset.find(
            (item) =>
              (item.id instanceof CubiqTS.Long ? item.id.toString() === mtc : item.id === mtc) ||
              item.constantName === mtc,
          )
        : null;
    return result != null ? result : null;
  }

  /* Moment */
  public isDateTimeValidMoment(dateTime: string, strict: boolean = true): boolean {
    try {
      const moment =
        dateTime != null && dateTime !== ""
          ? CubiqTS.moment(dateTime, this.config.dateTimeFormats, strict)
          : null;

      return moment?.isValid() === true;
    } catch (ex) {
      return false;
    }
  }

  public createMoment4String(input: string): CubiqTS.Moment | null {
    let inputArr: Array<string> = input.split(" ");
    return this.createMoment4StringArray(inputArr[0], inputArr[1], inputArr[2]);
  }
  /**
   *
   * @param inputArr array
   */
  public createMoment4StringArray(date: string, time: string, tz: string): CubiqTS.Moment | null {
    let dateTime = date + " " + time;
    if (this.isTimeZoneNameValid(tz) && this.isDateTimeValidMoment(dateTime)) {
      let javaTimeZoneCd: string = this.getTimeZoneImpl(tz).javaTimeZoneCd;
      let resultMoment =
        javaTimeZoneCd != null
          ? CubiqTS.moment.tz(dateTime, this.config.dateTimeFormats, javaTimeZoneCd)
          : null;
      return resultMoment != null && resultMoment.isValid() ? resultMoment : null;
    }
    return null;
  }

  public createMoment4Strings(date: string, time: string, tz: string): CubiqTS.Moment {
    if (date != null && date !== "" && time != null && time !== "" && tz != null && tz !== "") {
      try {
        let formatedTime =
          time.length === 5 && time.indexOf(":") == 2
            ? time
            : time.length === 4
              ? time.slice(0, 2) + ":" + time.slice(2)
              : time;
        let dateTime = date + " " + formatedTime;
        if (this.isTimeZoneNameValid(tz) && this.isDateTimeValidMoment(dateTime)) {
          let javaTimeZoneCd: string = this.getTimeZoneImpl(tz).javaTimeZoneCd;
          let resultMoment =
            javaTimeZoneCd != null
              ? CubiqTS.moment.tz(dateTime, this.config.dateTimeFormats, javaTimeZoneCd)
              : null;
          return resultMoment != null && resultMoment.isValid() ? resultMoment : null;
        } else {
          return null;
        }
      } catch (ex) {
        logger.out.error("ERROR: _oeWorker.createMoment4String(): ", ex);
        return null;
      }
    }
    return null;
  }

  public getTimeForNowMomentWithTimezone(tz: string, format: string = "HH:mm"): string {
    let nowMoment = this.createNowMoment4Timezone(tz);
    if (nowMoment != null) {
      let time = nowMoment.format(format);
      return time;
    }
    return null;
  }

  public createNowMoment4Timezone(tz: string): CubiqTS.Moment {
    if (tz != null && tz !== "") {
      try {
        if (this.isTimeZoneNameValid(tz)) {
          let javaTimeZoneCd: string = this.getTimeZoneImpl(tz).javaTimeZoneCd;
          let resultMoment = javaTimeZoneCd != null ? CubiqTS.moment().tz(javaTimeZoneCd) : null;
          return resultMoment != null && resultMoment.isValid() ? resultMoment : null;
        } else {
          return null;
        }
      } catch (ex) {
        logger.out.error("ERROR: _oeWorker.createMoment4String(): ", ex);
        return null;
      }
    }
    return null;
  }

  public checkIfGoodFromUntilModelIsEmpty(model: ICGoodFromUntilModel): boolean {
    if (model != null && model instanceof ICGoodFromUntilModel) {
      if (
        (model.dateValue == null || model.dateValue === "") &&
        (model.timeValue == null || model.timeValue === "") &&
        (model.timezoneValue == null || model.timezoneValue === "") &&
        (model.marketValue == null ||
          model.marketValue === "" ||
          model.marketValue === "GTC" ||
          model.marketValue === "NOW")
      ) {
        // this could be have a label of GTC = but no value - which is still "valid"
        // we need the label so we are not trying to interpret "null" when loaded
        return true;
      } else {
        return false;
      }
    }
    return true;
  }

  public createMoment4Model(model: ICGoodFromUntilModel): CubiqTS.Moment | null {
    if (!this.checkIfGoodFromUntilModelIsEmpty(model) && model instanceof ICGoodFromUntilModel) {
      try {
        if (model.marketValue != null && model.marketValue === "CUSTOM") {
          return this.createMomentForCustomTime(model);
        } else {
          return this.createMomentForModel(model);
        }
      } catch (ex) {
        logger.out.error("ERROR: _oeUtility.createMoment4Model(): ", ex);
        return null;
      }
    }
    return null;
  }

  private createMomentForCustomTime(model: ICGoodFromUntilModel): CubiqTS.Moment {
    if (
      model.dateValue != null &&
      model.dateValue !== "" &&
      model.timeValue != null &&
      model.timeValue !== "" &&
      model.timezoneValue != null &&
      model.timezoneValue !== "" &&
      model.marketValue != null &&
      (model.marketValue === "CUSTOM" || model.marketValue === "NOW")
    ) {
      let resultMoment = null;
      let timeValue: string =
        model.timeValue instanceof ICTimeControlModel ? model.timeValue.timeValue : model.timeValue;
      let inputDateTime: string = `${model.dateValue} ${timeValue}`;
      let dataset = this.dataset.timeZoneImpls;
      let timeZoneImpl: CubiqTS.TimeZoneImpl =
        dataset != null && dataset.length > 0
          ? dataset.find((item) => item.id === model.timezoneValue)
          : null;

      if (timeZoneImpl != null && timeZoneImpl instanceof CubiqTS.TimeZoneImpl) {
        resultMoment = CubiqTS.moment.tz(
          inputDateTime,
          "DD-MMM-YYYY HH:mm",
          timeZoneImpl.javaTimeZoneCd,
        );
      } else {
        logger.out.error(
          `TimeZoneImpl not found for ${model.timezoneValue}! calculating without timezone...`,
        );
        resultMoment = CubiqTS.moment(inputDateTime, "DD-MMM-YY HH:mm");
      }
      return resultMoment;
    } else {
      throw `Invalid custom model: ${model}`;
    }
  }

  public createMomentForModel(model: ICGoodFromUntilModel): CubiqTS.Moment | null {
    let value: CubiqTS.Moment = null;

    if (model != null && (model.dayOrder === true || model.marketValue == "CUSTOM")) {
      let timeZoneImpl: CubiqTS.TimeZoneImpl = this.getTimeZoneImpl(model.timezoneValue);
      let inputDateTime: string = `${model.dateValue} ${model.timeValue}`;
      if (timeZoneImpl != null) {
        value = CubiqTS.moment.tz(inputDateTime, "DD-MMM-YYYY HH:mm", timeZoneImpl.javaTimeZoneCd);
      } else {
        value = CubiqTS.moment(inputDateTime, "DD-MMM-YY HH:mm");
      }
    } else {
      value = this.createMomentForMarketTimeConstantName(model.marketValue);
    }

    return value;
  }

  private createMomentForMarketTimeConstantName(marketTimeConstantName: string): CubiqTS.Moment {
    let dataset = this._marketTimeConstLookupService.getAll();

    if (
      marketTimeConstantName != null &&
      marketTimeConstantName &&
      dataset != null &&
      dataset.length > 0
    ) {
      let marketTimeConstantImpl: CubiqTS.MarketTimeConstantImpl =
        this.getMarketTimeConstantImpl(marketTimeConstantName);
      if (
        marketTimeConstantImpl != null &&
        marketTimeConstantImpl instanceof CubiqTS.MarketTimeConstantImpl
      ) {
        return this.createMomentForMarketTimeConstantImpl(marketTimeConstantImpl);
      } else {
        throw `marketTimeConstant ${marketTimeConstantName} not found!`;
      }
    } else {
      throw `Invalid marketTimeConstant name: ${marketTimeConstantName}, or dataset missing (size: ${dataset.length})!`;
    }
  }

  public createMomentForMarketTimeConstantImpl(
    marketTimeConstantImpl: CubiqTS.MarketTimeConstantImpl,
  ): CubiqTS.Moment {
    if (
      marketTimeConstantImpl != null &&
      marketTimeConstantImpl instanceof CubiqTS.MarketTimeConstantImpl
    ) {
      let resultMoment = null;
      let timeHourValue: number =
        marketTimeConstantImpl.constantTime && marketTimeConstantImpl.constantTime.low
          ? parseInt(marketTimeConstantImpl.constantTime.low.toString())
          : 0;
      let timeHourString: string = timeHourValue < 1000 ? "0" + timeHourValue : "" + timeHourValue;
      let timeString: string = CubiqTS.moment(timeHourString, "HH:mm").format("HH:mm");
      let dateString: string = null;
      let timeZoneImpl: CubiqTS.TimeZoneImpl = this.getTimeZoneImpl(
        marketTimeConstantImpl.timeZoneCd,
      );

      // set system configured date
      if (
        marketTimeConstantImpl.constantDate != null &&
        marketTimeConstantImpl.constantTime != null &&
        marketTimeConstantImpl.constantTime.low != null
      ) {
        dateString = CubiqTS.moment(marketTimeConstantImpl.constantDate).format("YYYY-MM-DD");
      }
      // check if type is DATE or TODAY and set today's date
      if (
        (marketTimeConstantImpl.constantDate == null &&
          marketTimeConstantImpl.constantType === CubiqTS.MarketTimeConstantType.DATE) ||
        marketTimeConstantImpl.constantType === CubiqTS.MarketTimeConstantType.TODAY
      ) {
        dateString = CubiqTS.moment().format("YYYY-MM-DD");
      }
      // check if type is TOMORROW and set tomorrow's date
      if (
        marketTimeConstantImpl.constantDate == null &&
        marketTimeConstantImpl.constantType === CubiqTS.MarketTimeConstantType.TOMORROW
      ) {
        dateString = CubiqTS.moment().add(1, "d").format("YYYY-MM-DD");
      }

      if (
        dateString != null &&
        timeString != null &&
        timeZoneImpl != null &&
        timeZoneImpl instanceof CubiqTS.TimeZoneImpl
      ) {
        resultMoment = CubiqTS.moment.tz(
          dateString + " " + timeString,
          "YYYY-MM-DD HH:mm",
          timeZoneImpl.javaTimeZoneCd,
        );
      } else if (dateString != null && timeString != null) {
        logger.out.error(
          `TimeZoneImpl not found for ${marketTimeConstantImpl.timeZoneCd}! calculating without timezone...`,
        );
        resultMoment = CubiqTS.moment(dateString + " " + timeString, "YYYY-MM-DD HH:mm");
      }
      return resultMoment;
    } else {
      throw new Error(`Invalid market time constant!`);
    }
  }

  // HACK for order-entry defaults
  public createModelFromDayOrGtcOrNowStringForGoodFromUntil(
    constantName: string,
  ): ICGoodFromUntilModel {
    if (constantName === "DAY" || constantName === "GTC" || constantName === "NOW") {
      let goodFromUntilModel = new ICGoodFromUntilModel();
      goodFromUntilModel.marketValue = constantName;
      goodFromUntilModel.isNow = constantName === "NOW";
      return goodFromUntilModel;
    } else {
      return null;
    }
  }

  public createModelFromMarketTimeConstantImpl(
    marketTimeConstantImpl: CubiqTS.MarketTimeConstantImpl,
  ): ICGoodFromUntilModel {
    // hack for loading 'GTC' default as it breaks on "let value: number = marketTimeConstantImpl.constantTime.low;" since null;
    if (
      marketTimeConstantImpl instanceof CubiqTS.MarketTimeConstantImpl &&
      (marketTimeConstantImpl.constantName === "GTC" ||
        marketTimeConstantImpl.constantName === "NOW" ||
        marketTimeConstantImpl.constantName === "DAY")
    ) {
      let goodFromUntilModel = new ICGoodFromUntilModel();
      goodFromUntilModel.marketValue = marketTimeConstantImpl.constantName;
      goodFromUntilModel.isNow = marketTimeConstantImpl.constantName === "NOW";
      goodFromUntilModel.dayOrder = marketTimeConstantImpl.constantName === "DAY";
      return goodFromUntilModel;
    }

    if (marketTimeConstantImpl instanceof CubiqTS.MarketTimeConstantImpl) {
      let goodFromUntilModel = new ICGoodFromUntilModel();
      goodFromUntilModel.marketValue = marketTimeConstantImpl.constantName;
      goodFromUntilModel.dayOrder = goodFromUntilModel.marketValue == "DAY";
      let value: number = marketTimeConstantImpl.constantTime.low;
      goodFromUntilModel.timeValue = value < 1000 ? "0" + value : "" + value; // CubiqTS.moment(marketTimeConstantImpl.constantTime.low, 'HH:mm').format('HHmm');
      goodFromUntilModel.timezoneValue = marketTimeConstantImpl.timeZoneCd;
      // handle the date
      if (marketTimeConstantImpl.constantDate != null) {
        goodFromUntilModel.dateValue = CubiqTS.moment(marketTimeConstantImpl.constantDate).format(
          "DD-MMM-YYYY",
        );
      } else {
        // if(marketTimeConst.constantDate == null){
        if (
          marketTimeConstantImpl.constantType === 0 ||
          marketTimeConstantImpl.constantType === 1
        ) {
          goodFromUntilModel.dateValue = CubiqTS.moment().format("DD-MMM-YYYY");
        } else if (marketTimeConstantImpl.constantType === 2) {
          goodFromUntilModel.dateValue = CubiqTS.moment().add(1, "d").format("DD-MMM-YYYY");
        } else {
          logger.out.error(
            "ERROR: _oeTimeMachine.createModelFromMarketTimeConstantImpl(): Invalid date on MarketTimeConstant!",
          );
          goodFromUntilModel.dateValue = null;
        }
      }
      return goodFromUntilModel;
    } else {
      throw new Error(`Invalid market time constant!`);
    }
  }

  /**
   * creates a model for next 1700 EST in active user's tz
   */
  createDefaultUnpopulatedModelForUserTz() {
    let tdrTime: string = this._sysConf.hardGetSystemConfig(SysFeatures.systemTDRTime) ?? "1700";
    let tdrZone: string = this._sysConf.hardGetSystemConfig(SysFeatures.systemTDRZone) ?? "EST";

    if (tdrTime.length > 2 && !tdrTime.includes(":")) {
      let index = tdrTime.length - 2;
      tdrTime = `${tdrTime.substring(0, index)}:${tdrTime.substring(index)}`;

      if (tdrTime.length == 4) {
        tdrTime = "0" + tdrTime;
      }
    }

    let now = CubiqTS.moment();
    let est: CubiqTS.TimeZoneImpl = this.getTimeZoneImpl(tdrZone);
    let userTz: CubiqTS.TimeZoneImpl = this.getTimeZoneImpl(this.activeUser?.timeZoneCd ?? tdrZone);
    let next1700 = CubiqTS.moment.tz(now.format(`YYYY-MM-DD`) + `T${tdrTime}`, est.javaTimeZoneCd);
    let value;

    while (now.isSameOrAfter(next1700)) {
      next1700 = next1700.add(1, "d");
    }

    value = next1700.clone().tz(userTz.javaTimeZoneCd);

    let goodFromUntilModel = new ICGoodFromUntilModel();
    goodFromUntilModel.marketValue = "DAY";
    goodFromUntilModel.isNow = false;
    goodFromUntilModel.dayOrder = true;
    goodFromUntilModel.dateValue = value.format("DD-MMM-YYYY");
    goodFromUntilModel.timeValue = value.format("HHmm");
    goodFromUntilModel.timezoneValue = this.activeUser?.timeZoneCd ?? tdrZone;
    goodFromUntilModel.defaultDayConstant = value;

    return goodFromUntilModel;
  }
}
